import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import { actions as pluginActions } from "components/PluginsPopover/Plugin.reducer";
import { actions as provisioningStateActions } from "components/ProvisioningStateDot/ProvisioningState.reducer";
import { actions as authActions } from "containers/Auth/Auth.reducer";
import { actions as bundleRepositoriesActions } from "containers/BundleRepositories/BundleRepositories.reducer";
import { actions as bundleActions } from "containers/Bundles/Bundles.reducer";
import { actions as messagesActions } from "containers/Messages/Messages.reducer";
import { actions as projectActions } from "containers/Projects/Projects.reducer";
import { actions as nonProjectActions } from "containers/Projects/components/ProjectTree/NonProjectRecords/NonProjectRecords.reducer";
import { useReconnectingWebsocket } from "containers/Resources/screens/ResourcesSingle/utils";
import { actions as configActions } from "containers/WorkspaceConfig/WorkspaceConfig.reducer";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReconnectingWebSocket from "reconnecting-websocket";
import { actions as dataStoreActions } from "utils/DataStore/DataStore.reducer";
import { setUiWebsocketDown } from "utils/NotificationManager/connectionState";
import { actions as notificationActions } from "utils/NotificationManager/NotificationManager.reducer";
import { getWebsocketError } from "utils/NotificationManager/NotificationManager.selectors";
import { useVisibilityTabChange } from "utils/useVisibilityTabChange";

interface WebsocketNotification {
    type: "info" | "success" | "warn" | "error";
    title: string;
    subtitle?: string;
    details?: string;
}

const MultiWebsocket = ({ isSignedIn }: { isSignedIn: boolean | null }) => {
    const dispatch = useDispatch();
    const hasWebsocketError = useSelector(getWebsocketError);
    const hasWebsocketErrorRef = useRef(hasWebsocketError);
    hasWebsocketErrorRef.current = hasWebsocketError;

    const { closeAllWebsocketConnections } = useVisibilityTabChange();
    const { getSocket, deleteSocket } = useReconnectingWebsocket();

    useEffect(() => {
        if (closeAllWebsocketConnections || !isSignedIn) {
            return;
        }
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        const websocketNamespace = typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE || "/api/latest";
        const websocketPath = `${websocketHost}${websocketNamespace}/ui`;

        const socket: ReconnectingWebSocket | null = getSocket(websocketPath);

        const onError = () => {
            // Keep the module-level mirror in sync so secondary live-data
            // subscriptions can suppress their redundant connection-error
            // toasts while the primary `/ui` socket is down (idempotent).
            setUiWebsocketDown(true);
            if (!hasWebsocketErrorRef.current) {
                dispatch({
                    type: notificationActions.SET_WEBSOCKET_NOTIFICATION_ERROR,
                    response: true,
                });
            }
        };

        // Clear the "connection lost" banner as soon as the socket (re)connects,
        // instead of waiting for the next server-pushed message. The `/ui` socket
        // is a persistent ReconnectingWebSocket, so this fires on every reconnect.
        // Without this, a reconnect on which the server does not promptly re-push a
        // snapshot (common on mobile after a network flap / tab resume) leaves the
        // banner up even though the connection is usable again.
        const onOpen = () => {
            setUiWebsocketDown(false);
            if (hasWebsocketErrorRef.current) {
                dispatch({
                    type: notificationActions.SET_WEBSOCKET_NOTIFICATION_ERROR,
                    response: false,
                });
            }
        };

        const onMessage = (event: MessageEvent) => {
            setUiWebsocketDown(false);
            if (hasWebsocketErrorRef.current) {
                dispatch({
                    type: notificationActions.SET_WEBSOCKET_NOTIFICATION_ERROR,
                    response: false,
                });
            }
            let data: any;
            try {
                data = JSON.parse(event.data);
            } catch (error) {
                console.error("Failed to parse websocket message", error);
                return;
            }
            if (data.hasOwnProperty("auth")) {
                dispatch({
                    type: authActions.STORE_USER_DATA,
                    response: data.auth.identity,
                });
                dispatch({
                    type: authActions.STORE_USER_PERMISSIONS,
                    response: data.auth.permissions,
                });
            }
            if (data.hasOwnProperty("project_plain_list")) {
                dispatch({
                    type: projectActions.STORE_PROJECT_LIST,
                    data: data.project_plain_list,
                });
            }
            if (data.hasOwnProperty("submitted_message_list")) {
                dispatch({
                    type: messagesActions.STORE_SUBMITTED_MESSAGE_DATA,
                    messages: data.submitted_message_list,
                });
            }
            if (data.hasOwnProperty("unsubmitted_message_list")) {
                dispatch({
                    type: messagesActions.STORE_UNSUBMITTED_MESSAGE_DATA,
                    messages: data.unsubmitted_message_list,
                });
            }
            if (data.hasOwnProperty("bundle_plain_list")) {
                dispatch({
                    type: bundleActions.STORE_BUNDLE_LIST,
                    data: data.bundle_plain_list,
                });
            }
            if (data.hasOwnProperty("workspace_configuration_plain_list")) {
                dispatch({
                    type: configActions.STORE_WORKSPACE_CONFIG,
                    configList: data.workspace_configuration_plain_list,
                });
            }
            if (data.hasOwnProperty("role_plain_list")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "rolesAutocomplete",
                    data: data.role_plain_list,
                });
            }
            if (data.hasOwnProperty("notification")) {
                let notification: WebsocketNotification | null = null;
                try {
                    notification = JSON.parse(data.notification);
                } catch (error) {
                    console.error("Failed to parse websocket notification", error);
                }

                if (notification != null) {
                    const { type, title, subtitle, details } = notification;

                    NotificationService[type](
                        <ExpandableNotification
                            title={title}
                            subtitle={subtitle}
                            details={details}
                        />
                    );
                }
            }
            if (data.hasOwnProperty("opened_records")) {
                dispatch({
                    type: nonProjectActions.STORE_NON_PROJECT_OPENED_RECORDS,
                    data: data.opened_records,
                });
            }
            if (data.hasOwnProperty("pinned_records")) {
                dispatch({
                    type: nonProjectActions.STORE_NON_PROJECT_PINNED_RECORDS,
                    data: data.pinned_records,
                });
            }
            if (data.hasOwnProperty("bundle_repository_plain_list")) {
                dispatch({
                    type: bundleRepositoriesActions.FETCH_BUNDLE_REPOSITORIES,
                    data: data.bundle_repository_plain_list,
                });
            }
            if (data.hasOwnProperty("plugin_plain_list")) {
                dispatch({
                    type: pluginActions.FETCH_PLUGIN_LIST,
                    data: data.plugin_plain_list,
                });
            }
            if (data.hasOwnProperty("connector_input_schemas")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "connectorInputSchemas",
                    data: data.connector_input_schemas,
                });
            }
            if (data.hasOwnProperty("schema")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "schema",
                    data: data.schema,
                });
            }
            if (data.hasOwnProperty("object_template_schema")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "objectTemplateSchema",
                    data: data.object_template_schema,
                });
            }
            if (data.hasOwnProperty("git_log_plain_list")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "gitLogPlainList",
                    data: data.git_log_plain_list,
                });
            }
            if (data.hasOwnProperty("bundle_state")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "bundle_state",
                    data: data.bundle_state,
                });
            }
            if (data.hasOwnProperty("git_write_lock")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "gitWriteLock",
                    data: data.git_write_lock,
                });
            }
            if (data.hasOwnProperty("trash_state")) {
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: "trashState",
                    data: data.trash_state,
                });
            }
            if (data.hasOwnProperty("provisioning_state")) {
                const customObjectProvisioningStateMap =
                    data.provisioning_state?.custom_object_plain_list?.reduce((acc, current) => {
                        acc[current.id] = current;
                        return acc;
                    }, {});
                dispatch({
                    type: provisioningStateActions.STORE_CUSTOM_OBJECT_PROVISIONING_STATE,
                    data: customObjectProvisioningStateMap,
                });
            }
        };

        socket?.addEventListener("open", onOpen);
        socket?.addEventListener("error", onError);
        socket?.addEventListener("message", onMessage);

        return () => {
            socket?.removeEventListener("open", onOpen);
            socket?.removeEventListener("error", onError);
            socket?.removeEventListener("message", onMessage);
            deleteSocket(websocketPath);
        };
    }, [dispatch, isSignedIn, closeAllWebsocketConnections, getSocket, deleteSocket]);

    return <></>;
};

export default MultiWebsocket;
